import React, { useEffect, useState } from 'react';
import CommandPanelImage from 'components/commandpanel/CommandPanelImage';
import {
  GetDisplayMode,
  history,
  isCtrlKeyPressed,
  isNullOrUndefined,
} from 'utils/Global';
import { useRMDispatch, useRMSelector } from 'redux/hooks';
import { selectNavigatorAvailableImagesKeys } from 'redux/selectors/StateSelectors';
import { getNavigatorCommandPanelImageIcons } from 'utils/Navigator/NavigatorUtils';
import {
  useNavigatorRegions,
  usePlateDrawaingIdentifiers,
  useUpdateCompositeImage,
} from './hooks';
import { getNavFilteredScore, getNavigatorImageKeysAsString } from './utils';
import { NavigatorProps } from './NavigatorThumbnail';
import { useAllDropImageParameters, useScoresForWells } from 'redux/hooks/experiment';
import { DropImageParameters, DropUrlParameters } from 'components/Experiment/constants';
import { setWellImagingSettingDetails } from 'redux/reducers/experimentDataReducer';
import { updateDropPath } from 'utils/ExperimentUtils';

const NavigatorRegions = (props: NavigatorProps) => {
  const { setMoveToCanvas, roi, wellNavigationDetails, dropImageParameters } = props;
  const dispatch = useRMDispatch();
  const navAvailableImages = useRMSelector(selectNavigatorAvailableImagesKeys);
  const updateCompositeImage = useUpdateCompositeImage();
  const allWellImagingSettingDetails = useAllDropImageParameters();
  const { wellNum, plateID } = wellNavigationDetails || {};
  const { cpId, dropNumber, inspection } = dropImageParameters || {};
  const selectDrawingIdentifiers = usePlateDrawaingIdentifiers();
  const plateDrawings = selectDrawingIdentifiers(plateID);
  const disabledImageRegions = plateDrawings?.plateDropRegions;
  const imageBatchId = inspection?.groupedInspections?.find(
    (grp) => grp.plateId === plateID,
  )?.imageBatchId;
  const [navigatorRegions, setNavigatorRegions] = useState([]);
  const displayMode = GetDisplayMode();
  const currentWellScores = useScoresForWells([
    {
      wellNumber: wellNum,
      plateId: plateID,
      dropNumber: dropNumber,
    },
  ]);

  useEffect(() => {
    const compositeKey = getNavigatorImageKeysAsString(
      wellNavigationDetails,
      dropImageParameters,
    );
    if (navAvailableImages.includes(compositeKey)) {
      setNavigatorRegions(useNavigatorRegions(compositeKey));
    }
  }, [navAvailableImages, wellNavigationDetails, roi, dropImageParameters]);

  useEffect(() => {
    updateCompositeImage(wellNavigationDetails, dropImageParameters);
  }, [displayMode]);

  function changeRegionName(rgName, ctrlKey) {
    if (!ctrlKey) {
      updateDropPath(history.location.pathname, [
        { index: DropUrlParameters.REGIONNAME, value: rgName },
      ]);
    }
    const navigationDetails: DropImageParameters = {
      ...dropImageParameters,
      regionName: rgName,
    };

    updateCompositeImage(wellNavigationDetails, navigationDetails);

    const selectedNav = allWellImagingSettingDetails.find(
      (w) =>
        w.dropNumber === navigationDetails.dropNumber &&
        w.cpId === navigationDetails.cpId &&
        w.inspection === navigationDetails.inspection &&
        w.regionName === rgName,
    );

    if (
      allWellImagingSettingDetails.length === 1 &&
      !isNullOrUndefined(selectedNav) &&
      ctrlKey
    ) {
      return;
    }
    const filteredNavDetails = allWellImagingSettingDetails.filter(
      (w) =>
        w.dropNumber === navigationDetails.dropNumber &&
        w.cpId === navigationDetails.cpId &&
        w.inspection.groupedInspections.find((i) => i.imageBatchId === imageBatchId) &&
        w.regionName !== navigationDetails.regionName,
    );

    if (!isNullOrUndefined(selectedNav) && filteredNavDetails.length && ctrlKey) {
      dispatch(setWellImagingSettingDetails([...filteredNavDetails]));
      return;
    }
    if (ctrlKey) {
      if (allWellImagingSettingDetails.length < 4) {
        dispatch(
          setWellImagingSettingDetails([...filteredNavDetails, navigationDetails]),
        );
      }
      setMoveToCanvas(true);
    } else {
      dispatch(setWellImagingSettingDetails([navigationDetails]));
    }
  }

  return (
    <div id="navigatorregions-container" className="tabContainer">
      {navigatorRegions
        ? navigatorRegions.map((region) => (
            <CommandPanelImage
              image={region.image}
              inspectionName={region.name}
              imageName={region.imageName}
              inspectionDateTime={region.dateImaged}
              key={region.ImageIndex}
              active={
                allWellImagingSettingDetails.find(
                  (details) => details.regionName === region.imageName,
                )
                  ? true
                  : false
              }
              onClick={(e) => changeRegionName(region.imageName, isCtrlKeyPressed(e))}
              iconImages={getNavigatorCommandPanelImageIcons(
                plateDrawings,
                disabledImageRegions,
                wellNum,
                dropNumber,
                region.imageName,
                cpId,
                imageBatchId,
              )}
              currentWellScores={getNavFilteredScore(
                currentWellScores,
                wellNum,
                dropNumber,
                imageBatchId,
              )}
            />
          ))
        : null}
    </div>
  );
};

export default NavigatorRegions;
